import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, BadgeCheckIcon, MapPinIcon, ScaleIcon } from 'lucide-react';
import { Badge } from '../../ui/Badge';
import { Button } from '../../ui/Button';
import { SaveButton } from '../SaveButton';
import { UniversityMark } from '../UniversityMark';
import { useDiscover } from '../../../contexts/DiscoverContext';
import { campusImages, universityBlurbs } from '../../../data/discoverVisual';
import { deadlineState, nextDeadline } from '../../../utils/discover';
import { programmes as allProgrammes } from '../../../data/admissions';
import { cn } from '../../../utils/cn';
import type { UniversityProfile } from '../../../types/admissions';

/**
 * The Design A institution card: photography leading, then who it is, then what
 * you could study there, then what to do next. It is wide rather than tall so
 * three facts can sit side by side without any of them being squeezed.
 */
interface UniversityRowCardProps {
  university: UniversityProfile;
}
export function UniversityRowCard({
  university
}: UniversityRowCardProps) {
  const {
    isSaved,
    toggleSave,
    isCompared,
    toggleCompare,
    compareFull
  } = useDiscover();
  const owned = allProgrammes.filter(programme => programme.universityId === university.id);
  const soonest = nextDeadline(owned);
  const state = soonest ? deadlineState(soonest.deadline) : null;
  const compared = isCompared(university.id);
  return <article className="group overflow-hidden rounded-card border border-line bg-surface transition-[box-shadow,border-color] duration-200 ease-out hover:border-brand-200 hover:shadow-[0_12px_36px_rgba(15,23,42,0.08)]" data-id="element-1155" data-mp="21lzzwoyjshx2">
      <div className="grid gap-0 sm:grid-cols-[13rem_minmax(0,1fr)] lg:grid-cols-[16rem_minmax(0,1fr)]" data-id="element-1156" data-mp="1rulxo9f7p408">
        <div className="relative h-44 overflow-hidden sm:h-full" data-id="element-1157" data-mp="1wrvs63hgk5ux">
          <img src={campusImages[university.id]} alt={`The ${university.name} campus`} loading="lazy" className="h-full w-full object-cover transition-transform duration-300 ease-out group-hover:scale-[1.04]" data-id="element-1158" data-mp="2kiri0v2csqzj" />
        </div>

        <div className="min-w-0 p-5" data-id="element-1159" data-mp="2u6vlzasat8nt">
          <div className="grid gap-5 lg:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)]" data-id="element-1160" data-mp="2u7yhxx4ha1al">
            <div className="min-w-0" data-id="element-1161" data-mp="2p6hgo0i30cth">
              <div className="flex items-start gap-3" data-id="element-1162" data-mp="2l6h0aabf9rjs">
                <UniversityMark university={university} data-id="element-1163" data-mp="2l5xj4vtlj76t" />
                <div className="min-w-0" data-id="element-1164" data-mp="2g7w0gc0319vv">
                  <h3 className="text-h4 font-semibold tracking-tight text-ink" data-id="element-1165" data-mp="2g8yomc60sy83">
                    <Link to={`/discover/universities/${university.id}`} className="rounded outline-none hover:underline focus-visible:underline" data-id="element-1166" data-mp="2bfmcv2mfwlwr">
                      {university.name}
                    </Link>
                  </h3>
                  <p className="mt-1 flex items-center gap-1.5 text-body-sm text-ink-muted" data-id="element-1167" data-mp="1xfuw4u4jgrf2">
                    <MapPinIcon aria-hidden className="h-4 w-4 shrink-0" data-id="element-1168" data-mp="1siuoies1jglu" />
                    {university.city}, {university.country}
                  </p>
                </div>
              </div>

              <div className="mt-3 flex flex-wrap items-center gap-2" data-id="element-1169" data-mp="cxspgsb984o1">
                {university.provenance === 'verified' ? <Badge tone="ok" icon={<BadgeCheckIcon className="h-3.5 w-3.5" data-id="element-1171" data-mp="gym9halkszbn" />} data-id="element-1170" data-mp="h18hfbcdpgue">
                    Verified
                  </Badge> : <Badge tone="warn" data-id="element-1172" data-mp="3lfgibx2sjzxk">Entered by students</Badge>}
                <Badge plain data-id="element-1173" data-mp="3pfqwhx0qlwni">{university.type}</Badge>
                {university.internationalIntake && <Badge plain data-id="element-1174" data-mp="3uflne0kiim8k">International intake</Badge>}
                {state && !state.closed && <Badge tone={state.tone === 'neutral' ? 'neutral' : state.tone} plain data-id="element-1175" data-mp="30eayy8w5pyd">
                    Applications open
                  </Badge>}
              </div>

              <p className="mt-3 max-w-[62ch] text-body-sm leading-6 text-ink-muted" data-id="element-1176" data-mp="36pr8zekjopm7">
                {universityBlurbs[university.id]}
              </p>
            </div>

            <div className="min-w-0 lg:border-l lg:border-line lg:pl-5" data-id="element-1177" data-mp="1d4xs1ga4fd54">
              <h4 className="text-body-sm font-semibold text-ink" data-id="element-1178" data-mp="1898n0xfc141f">Popular programmes</h4>
              {owned.length === 0 ? <p className="mt-2 text-body-sm text-ink-muted" data-id="element-1179" data-mp="1m1gk6qaj2x0n">
                  None published on Campuzon yet.
                </p> : <ul className="mt-2 space-y-1.5" data-id="element-1180" data-mp="1vwgv1n45ar75">
                  {owned.slice(0, 3).map(programme => <li key={programme.id} className="flex gap-2 text-body-sm text-ink-muted" data-id="element-1181" data-mp="1qxngrsh1aw5x">
                      <span aria-hidden className="text-brand-700" data-id="element-1182" data-mp="24wwvsoti7k8b">
                        ·
                      </span>
                      <Link to={`/discover/programmes/${programme.id}`} className="truncate hover:text-ink hover:underline" data-id="element-1183" data-mp="20xe8xni4gz2u">
                        {programme.title}
                      </Link>
                    </li>)}
                </ul>}
              <p className="cz-num mt-2.5 text-caption text-ink-subtle" data-id="element-1184" data-mp="grz1nq8fqhh">
                {university.scholarshipsAvailable} scholarships listed
              </p>
              {soonest && <p className="cz-num mt-1 text-caption font-semibold text-danger-700" data-id="element-1185" data-mp="3nt2row0kukm5">
                  {soonest.deadlineLabel}
                </p>}
            </div>
          </div>

          <div className="mt-5 flex flex-wrap items-center gap-2 border-t border-line pt-4" data-id="element-1186" data-mp="3duwq6fionght">
            <SaveButton saved={isSaved('university', university.id)} onToggle={() => toggleSave('university', university.id)} subject={university.name} shape="heart" withLabel data-id="element-1187" data-mp="2zvdkdtdcgh8o" />
            <button type="button" onClick={() => toggleCompare(university.id)} disabled={!compared && compareFull} aria-pressed={compared} className={cn('inline-flex min-h-touch items-center gap-2 rounded-lg border px-3.5 text-body-sm font-medium transition-colors duration-150 ease-out disabled:cursor-not-allowed disabled:text-ink-subtle', compared ? 'border-brand-700 bg-brand-50 text-brand-800' : 'border-line bg-surface text-ink-muted hover:border-line-strong hover:text-ink')} data-id="element-1188" data-mp="y4k0itwbpduv">
              <ScaleIcon aria-hidden className="h-5 w-5" data-id="element-1189" data-mp="1fct8i5ff65xl" />
              {compared ? 'In comparison' : 'Compare'}
            </button>
            <Link to={`/discover/universities/${university.id}`} className="ml-auto" data-id="element-1190" data-mp="1albfc3jn19n6">
              <Button variant="primary" icon={<ArrowRightIcon className="h-5 w-5" data-id="element-1192" data-mp="15n9qt33g0i4b" />} data-id="element-1191" data-mp="15mi5qm7h22sa">
                Explore programmes
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </article>;
}